/**
 * Implementação do Repository de Analytics
 * Seguindo Repository Pattern e Dependency Inversion Principle
 */

import { IAnalyticsRepository } from '../../domain/interfaces/IAnalyticsRepository';
import {
  DemographicsStats,
  EmotionStats,
  HourlyStats,
  SystemStatsComplete,
} from '../../domain/entities/Analytics';
import { APIConfig } from '../config/APIConfig';

const apiClient = APIConfig.getInstance();

/**
 * Implementação concreta do repositório de analytics
 * Comunica com a API usando Axios
 */
export class AnalyticsRepository implements IAnalyticsRepository {
  /**
   * Buscar estatísticas completas do sistema
   */
  async getSystemStats(): Promise<SystemStatsComplete> {
    try {
      const response = await apiClient.get<SystemStatsComplete>(
        '/api/v1/stats/system',
        { timeout: 60000 } // 60s — query all-time pode ser lenta no primeiro hit (antes do cache 5min)
      );
      return response.data;
    } catch (error) {
      console.error('[AnalyticsRepository] Error fetching system stats:', error);
      throw error;
    }
  }

  /**
   * Buscar estatísticas demográficas
   */
  async getDemographics(params?: {
    camera_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<DemographicsStats> {
    try {
      const response = await apiClient.get<DemographicsStats>(
        '/api/v1/stats/demographics',
        { params, timeout: 30000 } // 30 segundos timeout
      );
      return response.data;
    } catch (error) {
      console.error('[AnalyticsRepository] Error fetching demographics:', error);
      throw error;
    }
  }

  /**
   * 🎭 Buscar estatísticas de emoções (feature premium)
   */
  async getEmotions(params?: {
    camera_id?: string;
    camera_ids?: string;
    scope?: 'zones'; // 🎭 Câmeras de zona (mapa de calor)
    period?: 'today' | 'week' | 'month' | 'custom';
    start_date?: string;
    end_date?: string;
  }): Promise<EmotionStats> {
    try {
      const response = await apiClient.get<EmotionStats>(
        '/api/v1/stats/emotions',
        { params, timeout: 30000 } // 30 segundos timeout
      );
      return response.data;
    } catch (error) {
      console.error('[AnalyticsRepository] Error fetching emotions:', error);
      throw error;
    }
  }

  /**
   * Buscar estatísticas por hora
   */
  async getHourlyStats(params?: {
    camera_id?: string;
    date?: string;
  }): Promise<HourlyStats> {
    try {
      const response = await apiClient.get<HourlyStats>(
        '/api/v1/stats/hourly',
        { params, timeout: 30000 } // 30 segundos timeout
      );
      return response.data;
    } catch (error) {
      console.error('[AnalyticsRepository] Error fetching hourly stats:', error);
      throw error;
    }
  }
}
